import _ from 'lodash';
import { createSelector } from 'reselect';

import { FORBIDDEN_PERMISSION_ERROR_CODES } from '../../constants/ErrorsConstants';
import { isEmpty } from '../../utils/ramdaUtils';
import { FORM_CHANGES_TABLE_DEFAULT_ROWS } from '../constants/runtime.constants';

export const getComponentProps = (_, props) => props || {};

export const getFormData = (state, props) => state.formDesigner[props?.formKey]?.data ?? {};
export const getEntityData = (state, props) =>
  state.formDesigner[props?.formKey]?.data?.entityDataStore?.entityData ?? {};
export const getUserContext = (state, props) =>
  state.formDesigner[props?.formKey]?.data?.formInfo?.userContext ?? {};
export const getFormModel = (state, props) => state.formDesigner[props?.formKey]?.formModel ?? {};
export const getFormInfo = (state, props) => state.formDesigner[props?.formKey]?.formInfo ?? {};
export const getWorkflowInfo = (state, props) =>
  state.formDesigner[props?.formKey]?.workflowInfo ?? {};
export const getDataSources = (state, props) =>
  state.formDesigner[props?.formKey]?.dataSources ?? {};
export const getEntityDefinition = (state, props) =>
  state.formDesigner[props?.formKey]?.entityDefinition ?? {};
export const getEditor = (state) => state.formDesigner.EDITOR;
export const getOriginalData = (state, props) =>
  state.formDesigner[props?.formKey]?.originalData ?? {};
export const getOriginalFormModel = (state, props) =>
  state.formDesigner[props?.formKey]?.originalFormModel ?? {};
export const getOriginalDataSources = (state, props) =>
  state.formDesigner[props?.formKey]?.originalDataSources ?? {};
export const getSessionFormModel = (state, props) =>
  state.formDesigner[props?.formKey]?.sessionFormModel ?? {};
export const getEntityType = (state, props) => Object.keys(getEntityDefinition(state, props))[0];
export const getFormEntityKey = (state, props) =>
  state.formDesigner[props?.formKey]?.formInfo?.formEntityKey;
export const getDeltaChanges = (state, props) =>
  state.formDesigner[props?.formKey]?.deltaChanges ?? {};
export const getLayoutChanges = (state, props) =>
  state.formDesigner[props?.formKey]?.layoutChanges ?? {};
export const getAddedFormModelElements = (state, props) =>
  state.formDesigner[props?.formKey]?.AddedFieldsFormModel ?? [];
export const getDeletedFormModelElements = (state, props) =>
  state.formDesigner[props?.formKey]?.DeletedFieldsFormModel ?? [];
export const getUserMadeInteraction = (state, props) =>
  !!state.formDesigner[props?.formKey]?.userMadeInteraction;
export const getPrimaryKeysMap = (state, props) =>
  state.formDesigner[props?.formKey]?.primaryKeysMap ?? {};
export const getRelationShipKeysMap = (state, props) =>
  state.formDesigner[props?.formKey]?.relationShipKeysMap ?? {};
export const getActiveContainers = (state, props) =>
  state.formDesigner[props?.formKey]?.activeContainers ?? {};
export const getFormRules = (state, props) => state.formDesigner[props?.formKey]?.formRules ?? [];
export const getConfirmModalOpen = (state, props) =>
  state.formDesigner[props?.formKey]?.confirmModalOpen ?? false;
export const getSelectedSubGrids = (state, props) =>
  state.formDesigner[props?.formKey]?.selectedSubGrids ?? [];
export const getBulkEditSelectedIds = (state, props) =>
  state.formDesigner[props?.formKey]?.bulkEditData?.selectedIds ?? [];
export const getShouldExecuteSetDefaultActions = (state, props) =>
  state.formDesigner[props?.formKey]?.shouldExecuteSetDefaultActions;
export const getShouldSkipFormRulesEvaluation = (state, props) =>
  state.formDesigner[props?.formKey]?.shouldSkipFormRulesEvaluation;
export const getCachedSupportData = (state, props) =>
  state.formDesigner[props?.formKey]?.cachedSupportData ?? {};
export const getSupportData = (state, props) =>
  state.formDesigner[props?.formKey]?.supportData ?? {};
export const getUserActionPointer = (state, props) =>
  state.formDesigner[props?.formKey]?.userActions?.actionPointer ?? 0;
export const getUserActionsArray = (state, props) =>
  state.formDesigner[props?.formKey]?.userActions?.actions ?? [];
export const getIsFormChangesMode = (state, props) =>
  !!state.formDesigner[props?.formKey]?.isFormChangesMode;
export const getFormChangesTableRows = (state, props) =>
  state.formDesigner[props?.formKey]?.formChangesTableRows ?? FORM_CHANGES_TABLE_DEFAULT_ROWS;
export const getIsFormChangesTableLoading = (state, props) =>
  state.formDesigner[props?.formKey]?.isFormChangesTableLoading;
export const getSimilarRecordsByControlId = (state, props) =>
  state.formDesigner[props?.formKey]?.similarRecords?.[props.controlId] || {};
export const getIsSimilarRecordsDataFetched = (state, props) =>
  state.formDesigner[props?.formKey]?.similarRecords?.isDataFetched;
export const getSearchResult = (state, props) =>
  state.formDesigner[props?.formKey]?.searchResult ?? {};

// Others
export const getNetworkStatus = (state) => state.networkStatus.isOnline;
export const getOfflineDataEntities = (state) => state.offlineData.entities;
export const getOfflineSearchResults = (state) => state.offlineData.offlineSearchResults;
export const getUnsynchronizedEntityId = (state) => state.offlineData.unsynchronizedEntityId;
export const getEntitiesDownloadingStates = (state) => state.offlineData.downloadingStates;
export const getToken = (state) => (state.auth.token ? state.auth.token : '');
export const getIsLogonUserChanged = (state) => state.auth.isLogonUserChanged;
export const getSavedSearchResults = (state) => state.savedSearchResults;
export const getShowDeleteEntityOfflineData = (state) => state.dialogStates.deleteEntity.open;
export const getShowStorageSpaceDialog = (state) => state.dialogStates.storageSpace.open;
export const getDeleteEntityOfflineDataId = (state) => state.dialogStates.deleteEntity.entityId;
export const getOfflineSavedEntity = (entityId) => (state) =>
  state.offlineData.entities?.find((entity) => entity.id === entityId);
export const getTranslations = (state) => state.translations;
export const getEntityTranslations = (state) => state.translations?.Entity || {};
export const getIsDockToBottom = (state) => state.formDesigner.EDITOR?.panel?.isDockToBottom;
export const getTableWidth = (state) => state.formDesigner.EDITOR?.panel?.tableWidth;
export const getTableHeight = (state) => state.formDesigner.EDITOR?.panel?.tableHeight;
export const getIsSystemTranslationLoaded = (state) => !_.isEmpty(state.translations?.system);
export const getFavorites = (state) => state.favorites?.data;
export const getWarnings = (state) => state.warnings;
export const getErrors = (state) => state.errors;
export const getPanelLayout = (state) => state.panelLayout;
export const getUserRoles = (state) => state.userRoles;
export const getUserRights = (state) => state.userRights ?? [];

export const getDataSourcesList = createSelector([getDataSources], (dataSourcesObject) => {
  const dataSourceKeys = Object.keys(dataSourcesObject);
  return dataSourceKeys.map((key) => ({
    ...dataSourcesObject[key],
    dataSourceKey: key,
  }));
});

export const getEntityValues = createSelector([getFormData, getEntityType], (data, entityType) =>
  _.get(data, ['entityDataStore', 'entityData', entityType])
);

export const makeGetEntityPropertyKey = createSelector(
  [getEntityDefinition, getEntityType, getComponentProps],
  (entityDefinition, entityType, { propertyName }) =>
    _.get(_.get(entityDefinition, entityType), ['properties', propertyName])
);

export const makeGetEntityPropertyValue = createSelector(
  [makeGetEntityPropertyKey, getEntityValues],
  (entityPropertyKey, entityValues) => _.get(entityValues, entityPropertyKey)
);

export const hasVisibleWarning = (instanceId) => (state) =>
  state.warnings.some((warning) => warning.instanceId === instanceId && warning.warningEnabled);

export const isFormLoading = (formKey) => (state) =>
  isEmpty(state.formDesigner[formKey]?.formModel || {});

export const isMasterSetupLoading = (state) => state.masterSetupPage?.isLoading;

export const hasFetchError = (state) => {
  return state.errors.some((error) => error?.hasFetchError);
};

export const hasForbiddenPermissionError = createSelector([getErrors], (errors) =>
  errors.some((error) => FORBIDDEN_PERMISSION_ERROR_CODES.includes(error.code))
);

export const hasWarningsOrErrors = createSelector(
  [getWarnings, getErrors],
  (warnings, errors) => !!warnings.length || !!errors.length
);
